/**
 * Offline outbox: berichten die je tikte zonder netwerk, opgeslagen in
 * IndexedDB zodat ze een app-restart overleven. Bij iedere reconnect
 * (socket of `window online`-event) lopen we de queue door en sturen we
 * alles alsnog.
 *
 * Scope V1: alleen tekst-only berichten (+ reply). Bijlagen vereisen
 * Blob-caching en aparte retry-logica — V2-onderwerp. Tot dan: als je
 * een bericht-met-bijlage probeert te sturen zonder netwerk, krijg je
 * een error-toast en blijft de Composer staan zoals 'ie was.
 */

import { api, ApiError } from "./api";

const DB_NAME = "swift_outbox";
const STORE = "items";
const DB_VERSION = 1;

export type OutboxItem = {
  id: string; // local-uuid
  conversation_id: number;
  body: string;
  reply_to_message_id?: number;
  created_at: number; // epoch ms — voor weergavevolgorde
  attempt_count: number;
  last_error?: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const s = t.objectStore(STORE);
        const r = run(s);
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
        t.oncomplete = () => db.close();
        t.onerror = () => {
          db.close();
          reject(t.error);
        };
      }),
  );
}

/** Wordt door drainOutbox/Main.tsx aangeroepen om de queue zichtbaar te
 *  maken aan de UI. Falen we IndexedDB te openen (quota op, browser
 *  weigert, corrupte DB), dan loggen we expliciet zodat 't niet stilzwijgend
 *  verdwijnt — anders zou de UI de banner verstoppen en denken: "queue
 *  leeg, klaar". Gebruiker kan dan met onleesbare DB nog werken zonder
 *  z'n niet-verstuurde berichten kwijt te raken bij refresh. */
export async function listOutbox(): Promise<OutboxItem[]> {
  try {
    const items = (await tx("readonly", (s) => s.getAll() as IDBRequest<OutboxItem[]>)) ?? [];
    return items.sort((a, b) => a.created_at - b.created_at);
  } catch (err) {
    console.error("[outbox] listOutbox failed — queue mogelijk onbereikbaar", err);
    return [];
  }
}

export async function pushOutbox(item: OutboxItem): Promise<void> {
  await tx("readwrite", (s) => s.put(item));
}

export async function removeOutbox(id: string): Promise<void> {
  await tx("readwrite", (s) => s.delete(id));
}

/** True als de fetch-fout een netwerkfout was (server niet bereikt),
 *  false als de server antwoordde met een HTTP-error. Alleen netwerk-
 *  fouten zijn queueable — een 400/403 lost zichzelf niet op door te
 *  wachten. */
export function isQueueableError(err: unknown): boolean {
  if (err instanceof ApiError) return false;
  if (err instanceof TypeError) return true; // fetch reject ("Failed to fetch")
  if (err instanceof Error && /network|fetch/i.test(err.message)) return true;
  return false;
}

let draining = false;

/** Werkt de queue af, in volgorde, totdat een send mislukt of de queue
 *  leeg is. Bij netwerk-error halverwege: stop — de volgende reconnect
 *  pakt 'm op. Bij een ApiError (server antwoordde 4xx/5xx): verwijder
 *  het item, want retryen lost dat niet op. `onUpdate` wordt gepingd
 *  na elke verandering zodat de UI 'm uit de banner kan halen. */
export async function drainOutbox(onUpdate: () => void): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    const items = await listOutbox();
    for (const item of items) {
      try {
        await api.sendMessage(item.conversation_id, {
          body: item.body,
          reply_to_message_id: item.reply_to_message_id,
        });
        await removeOutbox(item.id);
        onUpdate();
      } catch (err) {
        if (isQueueableError(err)) {
          // Nog steeds geen verbinding — bumpen + abort, anders gaan we
          // alle volgende items voor niets proberen.
          await pushOutbox({
            ...item,
            attempt_count: item.attempt_count + 1,
            last_error: "netwerkfout",
          });
          onUpdate();
          return;
        }
        // Echte error van de server — kanaal verwijderd, te lange body,
        // wat dan ook. Bericht heeft geen toekomst meer: weghalen.
        await removeOutbox(item.id);
        onUpdate();
      }
    }
  } finally {
    draining = false;
  }
}

/** Genereert een korte unieke id voor een outbox-item. crypto.randomUUID
 *  als beschikbaar; anders een random + timestamp-combo. */
export function makeOutboxId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
